import React, {useEffect, useRef, useState} from "react";
import {Dialog} from "primereact/dialog";
import Icon from "./icon";
import {fileThumbnail, getFileExtension} from "../utils";
import {requestWorkspaceMediaUrl} from "../private-media";
import {useTranslate} from "../useTranslate";

const BROWSER_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", "gif", "bmp", "avif"]);
const MEDIA_SIZE_CLASS = {
    "2rem": "w-2rem h-2rem",
    "2.5rem": "w-3rem h-3rem",
    "3rem": "w-3rem h-3rem",
    "4rem": "w-4rem h-4rem",
    "10rem": "w-10rem h-10rem",
};

function isBrowserImage(file) {
    const extension = getFileExtension(file?.name || "").toLowerCase();
    return (String(file?.mime || "").startsWith("image/") && BROWSER_IMAGE_EXTENSIONS.has(extension))
        || BROWSER_IMAGE_EXTENSIONS.has(extension);
}

function WorkspaceMediaThumbnail({file, workspaceId, width = "2rem", preview = false, showImage = true, onError}) {
    const translate = useTranslate();
    const mountedRef = useRef(true);
    const onErrorRef = useRef(onError);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const fileIdentifier = file?.documentId ?? file?.id;
    const browserImage = isBrowserImage(file);
    const imageSizeClass = MEDIA_SIZE_CLASS[width] || MEDIA_SIZE_CLASS["2rem"];

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        let cancelled = false;
        setThumbnailUrl(null);
        setPreviewUrl(null);
        setPreviewVisible(false);

        if (!showImage || !browserImage || !fileIdentifier || !workspaceId) return () => undefined;

        requestWorkspaceMediaUrl({documentId: file?.documentId, id: file?.id}, workspaceId, {
            format: "original",
            disposition: "inline",
        }).then((url) => {
            if (mountedRef.current && !cancelled) setThumbnailUrl(url);
        }).catch((error) => onErrorRef.current?.(error));

        return () => {
            cancelled = true;
        };
    }, [browserImage, file?.documentId, file?.id, fileIdentifier, showImage, workspaceId]);

    const openPreview = async () => {
        if (!showImage || !preview || previewLoading || !browserImage || !fileIdentifier || !workspaceId) return;
        setPreviewLoading(true);
        try {
            const url = await requestWorkspaceMediaUrl({documentId: file?.documentId, id: file?.id}, workspaceId, {
                format: "original",
                disposition: "inline",
            });
            if (!mountedRef.current) return;
            setPreviewUrl(url);
            setPreviewVisible(true);
        } catch (error) {
            onErrorRef.current?.(error);
        } finally {
            if (mountedRef.current) setPreviewLoading(false);
        }
    };

    if (!browserImage) return fileThumbnail(file?.name || "file", null, width, true);
    if (!showImage) return <Icon icon="mi mi:image" className="text-400"/>;

    return (
        <>
            <div
                className={`flex align-items-center justify-content-center ${preview ? "cursor-pointer" : ""}`}
                role={preview ? "button" : undefined}
                tabIndex={preview ? 0 : undefined}
                aria-label={preview ? translate("media.preview-file") : undefined}
                aria-busy={previewLoading}
                onClick={openPreview}
                onKeyDown={(event) => {
                    if (preview && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        openPreview();
                    }
                }}
            >
                {thumbnailUrl ? (
                    <img
                        src={thumbnailUrl}
                        alt={file?.alternativeText || file?.name || translate("media.preview-file")}
                        className={`border-round object-fit-cover ${imageSizeClass}`}
                    />
                ) : <Icon icon="mi mi:image" className="text-400"/>}
            </div>
            {preview && (
                <Dialog
                    visible={previewVisible}
                    modal
                    dismissableMask
                    header={file?.name}
                    className="rtl w-11 md:w-8 lg:w-6"
                    contentClassName="flex align-items-center justify-content-center overflow-auto"
                    onHide={() => {
                        setPreviewVisible(false);
                        setPreviewUrl(null);
                    }}
                >
                    {previewUrl && (
                        <img
                            src={previewUrl}
                            alt={file?.alternativeText || file?.name || translate("media.preview-file")}
                            className="max-w-full max-h-full border-round"
                        />
                    )}
                </Dialog>
            )}
        </>
    );
}

export default WorkspaceMediaThumbnail;
